import { useRouterState } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

export function NavigationProgress() {
	const isLoading = useRouterState({
		select: (s) => s.isLoading,
	});

	const [show, setShow] = useState(false);
	const [progress, setProgress] = useState(0);
	const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// Debounce showing the bar to avoid flicker on fast navigations
	useEffect(() => {
		if (isLoading) {
			showTimerRef.current = setTimeout(() => {
				setShow(true);
			}, 150);
		} else {
			if (showTimerRef.current) {
				clearTimeout(showTimerRef.current);
				showTimerRef.current = null;
			}
			setShow(false);
		}
		return () => {
			if (showTimerRef.current) {
				clearTimeout(showTimerRef.current);
			}
		};
	}, [isLoading]);

	// Simulate NProgress-style incremental progress
	useEffect(() => {
		if (show) {
			setProgress(15);
			intervalRef.current = setInterval(() => {
				setProgress((prev) => {
					if (prev >= 90) return prev;
					const increment = Math.max(1, (90 - prev) * 0.1);
					return Math.min(90, prev + increment);
				});
			}, 200);
		} else {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			setProgress(100);
		}
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [show]);

	return (
		<AnimatePresence>
			{show && (
				<motion.div
					className="fixed top-0 left-0 right-0 z-[9999] h-0.5"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2 }}
				>
					<motion.div
						className="h-full bg-primary"
						initial={{ width: "0%" }}
						animate={{ width: `${progress}%` }}
						transition={{ width: { duration: 0.4, ease: "easeOut" } }}
					/>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
